import { useState, useEffect } from "react";
import Logs from "@/components/common/log/logs";
import { useGetLogs } from "@/queries/process";
import { useParams } from "react-router";
import SortDropdown from "@/pages/procesos/components/SortDropdown";
import {
  sortDirectionEnum,
  filterTypesEnum,
  type SortDirectionType,
  type SortOptionsTypes,
  type FilterTypeList,
  type CurrentFiltersType,
  type MultiEntitySelections,
} from "@/types/common/filters";
import { mergeFilters } from "@/utils/filters";

import Filter from "@/components/ui/filters";
import type { LogItemType } from "@/types/common/logs";
import { Skeleton } from "@/components/ui/skeleton";

// Extended log type for the API response
interface ApiLogResponse {
  id: number;
  description: string;
  created_at?: string;
  properties?: {
    attributes?: {
      name?: string;
      last_name?: string;
    };
  };
  causer?: {
    email?: string;
  };
  subject_type?: string;
  subject?: Record<string, unknown>;
}

const sortFields: SortOptionsTypes[] = [
  { label: "Fecha", value: "created_at" },
  // { label: "Usuario", value: "causer.name" },
];

const FilterFields: FilterTypeList[] = [
  {
    label: "Fecha",
    type: filterTypesEnum.dates,
    field: "created_at",
  },
];

export const History = () => {
  const { id } = useParams();

  const [filters, setFilters] = useState<CurrentFiltersType[]>([]);
  const [selectedEntities, setSelectedEntities] =
    useState<MultiEntitySelections>({});

  const [orderField, setOrderField] = useState<string>("created_at");
  const [orderDirection, setOrderDirection] = useState<SortDirectionType>(
    sortDirectionEnum.desc,
  );

  const queryFilters = {
    ...(filters.length > 0 ? mergeFilters(filters) : {}),
    entity_id: id,
  };

  const {
    data: logsResponse,
    error,
    isLoading,
  } = useGetLogs({
    filters: queryFilters,
    params: {
      order_by: orderField
        ? `${orderDirection === "desc" ? "!" : ""}${orderField}`
        : "!created_at",
      relationships: "causer,subject.userStates",
    },
  });

  const [recentLogs, setRecentLogs] = useState<LogItemType[]>([]);
  const [yesterdayLogs, setYesterdayLogs] = useState<LogItemType[]>([]);
  const [pastLogs, setPastLogs] = useState<LogItemType[]>([]);

  useEffect(() => {
    if (!logsResponse?.data) return;

    const now = new Date();
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);

    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(todayStart.getDate() - 1);

    const formatted: LogItemType[] = logsResponse.data.map(
      (log: ApiLogResponse, idx: number) => ({
        id: Number(log.id ?? idx),
        description: log.properties?.attributes?.name ?? "-",
        action: log.description ?? "-",
        author:
          log.causer?.email ??
          (log.properties?.attributes?.name
            ? `${log.properties.attributes.name} ${log.properties.attributes.last_name}`
            : "Desconocido"),
        date: log.created_at ? new Date(log.created_at) : new Date(),
        subject_type: log?.subject_type,
        subject: log?.subject ?? undefined,
      }),
    );

    const recent: LogItemType[] = [];
    const yesterday: LogItemType[] = [];
    const past: LogItemType[] = [];

    formatted.forEach((item) => {
      const date = new Date(item.date);
      if (date >= todayStart) {
        recent.push(item);
      } else if (date >= yesterdayStart && date < todayStart) {
        yesterday.push(item);
      } else {
        past.push(item);
      }
    });

    setRecentLogs(recent);
    setYesterdayLogs(yesterday);
    setPastLogs(past);
  }, [logsResponse]);

  const handleapplySort = (
    field: string | undefined,
    direction: SortDirectionType,
  ) => {
    if (!field) return;
    setOrderField(field);
    setOrderDirection(direction);
  };

  const renderBlocks = () => {
    const blocks = [
      { title: "Recientemente", items: recentLogs },
      { title: "Ayer", items: yesterdayLogs },
      { title: "Pasados", items: pastLogs },
    ];

    const sortedBlocks =
      orderDirection === sortDirectionEnum.asc ? blocks.reverse() : blocks;

    return sortedBlocks.map(
      (block) =>
        block.items.length > 0 && (
          <Logs key={block.title} title={block.title} items={block.items} />
        ),
    );
  };

  return (
    <>
      <div className="flex justify-between">
        <div className="flex space-x-3">
          <SortDropdown
            currentOrderField={orderField}
            currentOrderDirection={orderDirection}
            sortOptions={sortFields}
            onClick={handleapplySort}
          />
          <Filter
            filters={FilterFields}
            currentFilters={filters}
            onCurrentFiltersChange={setFilters}
            selecteds={selectedEntities}
            onSetSelecteds={setSelectedEntities}
          />
        </div>
      </div>

      <div className="bg-neutral-object p-4 rounded-lg border border-neutral-quiet [@media(max-height:800px)]:max-h-full h-[calc(100vh-265px)] overflow-y-auto">
        {isLoading ? (
          <div role="status" className="max-w-sm animate-pulse">
            <Skeleton className="h-3 rounded-full w-48 mb-4" />
            <Skeleton className="h-2 rounded-full max-w-[380px] mb-2.5" />
            <Skeleton className="h-2 rounded-full max-w-[340px] mb-2.5" />
            <Skeleton className="h-2 rounded-full max-w-[320px] mb-2.5" />
          </div>
        ) : error ? (
          <p className="text-danger">Error al cargar registros</p>
        ) : (
          <div className="space-y-10">{renderBlocks()}</div>
        )}
      </div>
    </>
  );
};
